import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { addDays, format } from 'date-fns';
import TimeRangeDisplay from './TimeRangeDisplay';

const Container = styled.div`
  padding: 1.5rem;
  font-family: sans-serif;
`;
const HoverDateTooltip = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: #212529;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 20;
`;

const DurationSlider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  width: 100%;
  height: 8px;
  border-radius: 3px;
  box-height: 10px;
  background: linear-gradient(
    to right,
    ${(props) => props.activeColor || '#2D8995'} 0%,
    ${(props) => props.activeColor || '#2D8995'}
      ${(props) => props.valuePercent}%,
    ${(props) => props.inactiveColor || '#A2A2A2'}
      ${(props) => props.valuePercent}%,
    ${(props) => props.inactiveColor || '#A2A2A2'} 100%
  );
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    height: 20px;
    width: 20px;
    background: ${(props) => props.activeColor || '#2D8995'};
    cursor: pointer;
    border-radius: 4px;
    border: none;
    margin-top: -1px;
  }

  &::-moz-range-thumb {
    height: 20px;
    width: 20px;
    background: ${(props) => props.activeColor || '#2D8995'};
    cursor: pointer;
    border-radius: 4px;
    border: none;
  }

  &::-ms-thumb {
    height: 20px;
    width: 20px;
    background: ${(props) => props.activeColor || '#2D8995'};
    cursor: pointer;
    border-radius: 4px;
    border: none;
  }
`;
const TopicBox = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 2rem;
  background: #fff;
`;

const TimelineWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: 2rem 0 2.5rem 0;
`;

const Timeline = styled.div`
  position: relative;
  height: 30px;
  background: #eee;
`;

const RangeBlock = styled.div`
  position: absolute;
  height: 100%;
  background: ${(props) => props.color || '#0b7285'};
  left: ${(props) => props.left}%;
  width: ${(props) => props.width}%;
  cursor: ${(props) => (props.draggable ? 'grab' : 'default')};
  user-select: none;
`;

const DragHandleLeft = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(-50%, -50%);
  width: 25px;
  height: 25px;
  background: white;
  border: 2px solid #0b7285;
  border-radius: 50%;
  cursor: ew-resize;
  user-select: none;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
`;

const DragHandleRight = styled.div`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translate(50%, -50%);
  width: 25px;
  height: 25px;
  background: white;
  border: 2px solid #0b7285;
  border-radius: 50%;
  cursor: ew-resize;
  user-select: none;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
  z-index: 10;
`;

const HiddenRangeInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const LabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  margin-top: 1.5rem;
  padding: 0 5px;
  pointer-events: none;
`;

const Label = styled.span`
  transform: translateX(-50%);
  white-space: nowrap;
  position: absolute;
`;
const RangeBlockWithTooltip = styled(RangeBlock)`
  &:hover ${HoverDateTooltip} {
    opacity: 1;
  }
`;
const DateRangeSlider = ({
  onChange,
  topicOneLabelStart = new Date('2023-01-01'),
  topicOneTotalDays = 365,
  topicTwoLabelStart = new Date('2023-01-01'),
  topicTwoTotalDays = 365,
}) => {
  const [duration, setDuration] = useState(topicOneTotalDays);
  const [topic1Start, setTopic1Start] = useState(0);
  const [topic2Start, setTopic2Start] = useState(0);

  const draggingDuration = useRef(false);
  const draggingLeftHandle = useRef(false);
  const draggingTopic2 = useRef(false);

  const dragStartXRight = useRef(0);
  const dragStartDuration = useRef(duration);
  const dragStartXLeft = useRef(0);
  const dragStartTopic1Start = useRef(topic1Start);
  const dragStartDurationLeft = useRef(duration);
  const dragStartTopic2X = useRef(0);
  const dragStartTopic2Start = useRef(topic2Start);

  const timelineRef = useRef(null);

  useEffect(() => {
    emitDates(topic1Start, duration, topic2Start, duration);
  }, [topic1Start, topic2Start, duration]);

  const emitDates = (t1Start, dur1, t2Start, dur2) => {
    const topic1From = addDays(topicOneLabelStart, Math.round(t1Start));
    const topic1To = addDays(topicOneLabelStart, Math.round(t1Start + dur1));
    const topic2From = addDays(topicTwoLabelStart, Math.round(t2Start));
    const topic2To = addDays(topicTwoLabelStart, Math.round(t2Start + dur2));

    const newRanges = {
      topic1: { from: topic1From, to: topic1To },
      topic2: { from: topic2From, to: topic2To },
    };

    setDateRanges(newRanges); // <-- update local state
    onChange?.(newRanges);
  };

  const onDragHandleRightMouseDown = (e) => {
    draggingDuration.current = true;
    dragStartXRight.current = e.clientX;
    dragStartDuration.current = duration;
    e.preventDefault();
  };

  const onDragHandleLeftMouseDown = (e) => {
    draggingLeftHandle.current = true;
    dragStartXLeft.current = e.clientX;
    dragStartTopic1Start.current = topic1Start;
    dragStartDurationLeft.current = duration;
    e.preventDefault();
  };

  const onTopic2DragStart = (e) => {
    draggingTopic2.current = true;
    dragStartTopic2X.current = e.clientX;
    dragStartTopic2Start.current = topic2Start;
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    const timelineWidthPx = timelineRef.current?.clientWidth || 300;
    const daysPerPixel1 = topicOneTotalDays / timelineWidthPx;
    const daysPerPixel2 = topicTwoTotalDays / timelineWidthPx;

    if (draggingLeftHandle.current) {
      const deltaX = e.clientX - dragStartXLeft.current;
      let newStart = Math.round(
        dragStartTopic1Start.current + deltaX * daysPerPixel1
      );
      newStart = Math.max(0, newStart);
      let newDuration = Math.round(
        dragStartDurationLeft.current +
          (dragStartTopic1Start.current - newStart)
      );
      newDuration = Math.min(
        newDuration,
        topicOneTotalDays - newStart,
        topicTwoTotalDays - topic2Start
      );
      setTopic1Start(newStart);
      setDuration(newDuration);
    }

    if (draggingDuration.current) {
      const deltaX = e.clientX - dragStartXRight.current;
      const daysPerPixel = Math.min(daysPerPixel1, daysPerPixel2);
      let newDuration = Math.round(
        dragStartDuration.current + deltaX * daysPerPixel
      );
      newDuration = Math.max(
        1,
        Math.min(
          newDuration,
          topicOneTotalDays - topic1Start,
          topicTwoTotalDays - topic2Start
        )
      );
      setDuration(newDuration);
    }

    if (draggingTopic2.current) {
      const deltaX = e.clientX - dragStartTopic2X.current;
      let newStart = Math.round(
        dragStartTopic2Start.current + deltaX * daysPerPixel2
      );
      newStart = Math.max(0, Math.min(newStart, topicTwoTotalDays - duration));
      setTopic2Start(newStart);
    }
  };

  const onMouseUp = () => {
    draggingLeftHandle.current = false;
    draggingDuration.current = false;
    draggingTopic2.current = false;
  };
  useEffect(() => {
    const max = Math.min(
      topicOneTotalDays - topic1Start,
      topicTwoTotalDays - topic2Start
    );
    if (duration > max) {
      setDuration(max);
    }
  }, [topic1Start, topic2Start, topicOneTotalDays, topicTwoTotalDays]);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  });
  const [dateRanges, setDateRanges] = useState({
    topic1: { from: topicOneLabelStart, to: topicOneLabelStart },
    topic2: { from: topicTwoLabelStart, to: topicTwoLabelStart },
  });

  const generateLabels = (labelStart, labelTotalDays) => {
    let formatStr = 'MMM yyyy';
    let numberOfLabels = 5;

    if (labelTotalDays <= 10) {
      numberOfLabels = labelTotalDays;
      formatStr = 'dd MMM'; // daily
    } else if (labelTotalDays <= 60) {
      numberOfLabels = 5;
      formatStr = 'dd MMM';
    } else if (labelTotalDays <= 730) {
      numberOfLabels = 6;
      formatStr = 'MMM yyyy';
    } else {
      numberOfLabels = 5;
      formatStr = 'yyyy';
    }

    numberOfLabels = Math.max(1, numberOfLabels);

    return Array.from({ length: numberOfLabels + 1 }, (_, i) => {
      const daysOffset = (i * labelTotalDays) / numberOfLabels;
      const labelDate = addDays(labelStart, daysOffset);
      return {
        label: format(labelDate, formatStr),
        position: (daysOffset / labelTotalDays) * 100,
      };
    });
  };

  const maxDuration = draggingTopic2.current
    ? topicOneTotalDays - topic1Start
    : Math.min(
        topicOneTotalDays - topic1Start,
        topicTwoTotalDays - topic2Start
      );
  const [durationInput, setDurationInput] = useState(duration);

  // Sync durationInput when duration changes via slider or drag
  useEffect(() => {
    setDurationInput(Math.round(duration));
  }, [duration]);

  const handleDurationInputChange = (e) => {
    const val = e.target.value;

    // Allow only numeric input
    if (/^\d*$/.test(val)) {
      setDurationInput(val);

      const num = parseInt(val, 10);
      if (!isNaN(num)) {
        const clamped = Math.min(Math.max(1, num), maxDuration);
        setDuration(clamped);
      }
    }
  };

  const commitDurationInput = () => {
    const num = parseInt(durationInput, 10);
    if (!isNaN(num)) {
      const clamped = Math.min(Math.max(1, num), maxDuration);
      setDuration(clamped);
    } else {
      setDurationInput(Math.round(duration)); // Reset to current duration on invalid input
    }
  };

  return (
    <Container>
      <div
        style={{
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Set Duration
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <DurationSlider
              min={1}
              max={maxDuration}
              value={Math.round(duration)}
              valuePercent={Math.min(
                (Math.round(duration) / maxDuration) * 100,
                100
              )}
              onChange={(e) => {
                const val = Number(e.target.value);
                setDuration(Math.min(Math.max(1, val), maxDuration));
              }}
            />

            <input
              type='text'
              value={durationInput}
              onChange={handleDurationInputChange}
              onBlur={commitDurationInput}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitDurationInput();
                  e.target.blur();
                }
              }}
              style={{
                border: '1px solid #D9D9D9',
                borderRadius: '6px',
                padding: '0.3rem 0.5rem',
                width: '48px',
                height: '33px',
                textAlign: 'center',
                boxShadow: '0px 0px 8px 0px #A2A2A23D',
                backgroundColor: '#fff',
              }}
            />

            <span>Days</span>
          </div>
        </div>
        <TimeRangeDisplay
          topic1={dateRanges.topic1}
          topic2={dateRanges.topic2}
        />
      </div>

      <TopicBox>
        <strong>Topic 1</strong>
        <TimelineWrapper>
          <Timeline ref={timelineRef}>
            <RangeBlockWithTooltip
              left={(topic1Start / topicOneTotalDays) * 100}
              width={(duration / topicOneTotalDays) * 100}
              draggable
              color='#0b7285'
            >
              <HoverDateTooltip>
                {format(dateRanges.topic1.from, 'dd/MM/yyyy')} –{' '}
                {format(dateRanges.topic1.to, 'dd/MM/yyyy')}
              </HoverDateTooltip>
              <DragHandleLeft onMouseDown={onDragHandleLeftMouseDown} />
              <DragHandleRight onMouseDown={onDragHandleRightMouseDown} />
            </RangeBlockWithTooltip>
          </Timeline>
          <LabelsRow>
            {generateLabels(topicOneLabelStart, topicOneTotalDays).map(
              ({ label, position }, i) => (
                <Label key={i} style={{ left: `${position}%` }}>
                  {label}
                </Label>
              )
            )}
          </LabelsRow>
        </TimelineWrapper>
      </TopicBox>

      <HiddenRangeInput
        type='range'
        min={0}
        max={topicOneTotalDays - duration}
        value={Math.round(topic1Start)}
        onChange={(e) =>
          setTopic1Start(
            Math.max(
              0,
              Math.min(Number(e.target.value), topicOneTotalDays - duration)
            )
          )
        }
      />

      <TopicBox>
        <strong>Topic 2</strong>
        <TimelineWrapper>
          <Timeline>
            <RangeBlockWithTooltip
              left={(topic2Start / topicTwoTotalDays) * 100}
              width={(duration / topicTwoTotalDays) * 100}
              draggable
              color='#94d82d'
              onMouseDown={onTopic2DragStart}
            >
              <HoverDateTooltip>
                {format(dateRanges.topic2.from, 'dd/MM/yyyy')} –{' '}
                {format(dateRanges.topic2.to, 'dd/MM/yyyy')}
              </HoverDateTooltip>
            </RangeBlockWithTooltip>
          </Timeline>
          <LabelsRow>
            {generateLabels(topicTwoLabelStart, topicTwoTotalDays).map(
              ({ label, position }, i) => (
                <Label key={i} style={{ left: `${position}%` }}>
                  {label}
                </Label>
              )
            )}
          </LabelsRow>
        </TimelineWrapper>
      </TopicBox>

      <HiddenRangeInput
        type='range'
        min={0}
        max={topicTwoTotalDays - duration}
        value={Math.round(topic2Start)}
        onChange={(e) =>
          setTopic2Start(
            Math.max(
              0,
              Math.min(Number(e.target.value), topicTwoTotalDays - duration)
            )
          )
        }
      />
    </Container>
  );
};

export default DateRangeSlider;
